import { useMemo } from "react";
import cx from "classnames";
import styles from "./AvgQaScoreFormatter.module.scss";
import { fixToTwo } from "./NumberFormatter";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Flex, Text } from "@radix-ui/themes";
import { Scorecard } from "@/v2/entities/explore/Explore.model";

function ScorecardsAvgQaScoreInfo({ scorecards }: { scorecards: Scorecard[] }) {
  return (
    <>
      {scorecards.map((scorecard) => (
        <Flex key={scorecard.id} direction={"column"} gap={"1"}>
          <Flex gap={"2"}>
            <Text size={"2"} weight={"bold"}>
              {scorecard.name}
            </Text>
          </Flex>

          {scorecard.avgQaScore != null && (
            <Text size={"2"}>
              <AvgQaScoreFormatter value={Number(scorecard.avgQaScore)} />
            </Text>
          )}
        </Flex>
      ))}
    </>
  );
}

export function AvgQaScoreFormatter({
  value,
  scorecards,
}: {
  value?: number;
  scorecards?: Scorecard[];
}) {
  const qaScore = useMemo(() => {
    return value ?? 0;
  }, [value]);

  const color = useMemo(() => {
    if (qaScore < 2) {
      return "red";
    } else if (qaScore <= 4) {
      return "yellow";
    } else {
      return "green";
    }
  }, [qaScore]);

  if (value == null) {
    return (
      <Text
        color="gray"
        weight={"bold"}
        highContrast
        className={styles.qaScoreValue}
      >
        -
      </Text>
    );
  }

  if (!scorecards || !scorecards.length) {
    return (
      <div className={styles.qaScore}>
        {[...Array(5)].map((_, index) => (
          <span
            key={index}
            className={cx(styles.qaScoreDot, {
              [styles[`${color}`]]: index + 1 <= qaScore,
              [styles[`half_${color}`]]: index < qaScore && qaScore < index + 1,
            })}
          />
        ))}

        <Text
          color="gray"
          weight={"bold"}
          highContrast
          className={styles.qaScoreValue}
        >
          {fixToTwo(value)}
        </Text>
      </div>
    );
  } else {
    return (
      <HoverCard>
        <HoverCardTrigger>
          <div className={styles.qaScore}>
            {[...Array(5)].map((_, index) => (
              <span
                key={index}
                className={cx(styles.qaScoreDot, {
                  [styles[`${color}`]]: index + 1 <= qaScore,
                  [styles[`half_${color}`]]:
                    index < qaScore && qaScore < index + 1,
                })}
              />
            ))}

            <Text
              color="gray"
              weight={"bold"}
              highContrast
              className={styles.qaScoreValue}
            >
              {fixToTwo(value)}
            </Text>
          </div>
        </HoverCardTrigger>

        <HoverCardContent side="top" align="start">
          <ScorecardsAvgQaScoreInfo scorecards={scorecards} />
        </HoverCardContent>
      </HoverCard>
    );
  }
}
